
import React, { memo, useEffect } from 'react';
import { LoadingState, LoadingStateProps } from './loading-state';
import { trackComponentRender } from '@/utils/performance/memoryLeakDetector';

/**
 * StandardizedLoadingState - A consistent loading experience component
 * 
 * This component wraps the LoadingState component to provide a consistent
 * loading experience throughout the application with performance tracking.
 */
export interface StandardizedLoadingStateProps extends LoadingStateProps {
  /**
   * Custom loading context for analytics
   */
  context?: string;
  
  /**
   * ID for testing
   */
  testId?: string;
  
  /**
   * Optional callback fired when loading starts
   */
  onLoadingStart?: () => void;
  
  /**
   * Optional callback fired when loading ends (component unmounts)
   */
  onLoadingEnd?: () => void;
}

export const StandardizedLoadingState = memo(({
  message = "Loading...",
  size = "default",
  centered = true,
  variant = "spinner",
  className = "",
  context,
  testId,
  onLoadingStart,
  onLoadingEnd,
  ...props
}: StandardizedLoadingStateProps) => {
  // Generate a unique instance ID for tracking
  const instanceId = React.useMemo(() => 
    `loading_${context || 'general'}_${Math.random().toString(36).substring(2, 9)}`, 
    [context]
  );
  
  // Track performance metrics in both development and production
  useEffect(() => {
    // Track component render for performance monitoring
    trackComponentRender('StandardizedLoadingState', instanceId, 0);
    
    // Log for development and call the onLoadingStart callback
    if (process.env.NODE_ENV === 'development' && context) {
      console.log(`[Loading][${context}] Started at ${new Date().toISOString()}`);
    }
    
    if (onLoadingStart) {
      onLoadingStart();
    }
    
    return () => {
      // Clean up on unmount
      if (process.env.NODE_ENV === 'development' && context) {
        console.log(`[Loading][${context}] Ended at ${new Date().toISOString()}`);
      }
      
      if (onLoadingEnd) {
        onLoadingEnd();
      }
    };
  }, [context, instanceId, onLoadingStart, onLoadingEnd]);
  
  return (
    <LoadingState
      message={message}
      size={size}
      centered={centered}
      variant={variant}
      className={`standardized-loading ${className}`}
      data-testid={testId || 'standardized-loading'}
      data-loading-context={context}
      {...props}
    />
  );
});

StandardizedLoadingState.displayName = 'StandardizedLoadingState';

export default StandardizedLoadingState;
